import React, { useEffect, useRef, useState } from 'react'
import { addJobComment, fetchJobComments, type JobComment } from '../../api/jobWorkflow'

interface JobCommentsThreadProps {
  jobId: string
  /** Focus the note composer after mount (e.g. mobile quick action). */
  autoFocus?: boolean
  /** Hide the composer; comments only (e.g. Overview preview). */
  readOnly?: boolean
  /** When set, show only the most recent N comments. */
  limit?: number
  /** Tighter list spacing / shorter scroll for previews. */
  compact?: boolean
}

export function JobCommentsThread({
  jobId,
  autoFocus,
  readOnly,
  limit,
  compact,
}: JobCommentsThreadProps) {
  const [comments, setComments] = useState<JobComment[]>([])
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const composerRef = useRef<HTMLTextAreaElement>(null)

  const load = async () => {
    setLoading(true)
    try {
      setComments(await fetchJobComments(jobId))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [jobId])

  useEffect(() => {
    if (!autoFocus || readOnly || loading) return
    const t = window.setTimeout(() => composerRef.current?.focus(), 120)
    return () => window.clearTimeout(t)
  }, [autoFocus, readOnly, loading])

  const handleSend = async () => {
    if (!body.trim()) return
    await addJobComment(jobId, body.trim())
    setBody('')
    await load()
  }

  if (loading) return <p className="text-sm text-gray-500">Loading comments...</p>

  const visible =
    typeof limit === 'number' && limit > 0 ? comments.slice(-limit) : comments
  const hiddenCount =
    typeof limit === 'number' && limit > 0 ? Math.max(0, comments.length - visible.length) : 0

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      <div className={`space-y-3 ${compact ? 'max-h-48' : 'max-h-80'} overflow-y-auto`}>
        {visible.length === 0 && <p className="text-sm text-gray-500">No comments yet.</p>}
        {hiddenCount > 0 && (
          <p className="text-xs text-gray-500">
            {hiddenCount} older comment{hiddenCount === 1 ? '' : 's'} not shown
          </p>
        )}
        {visible.map((c) => (
          <div key={c.id} className={`bg-gray-50 rounded-lg ${compact ? 'p-2.5' : 'p-3'}`}>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{c.body}</p>
            <p className="text-xs text-gray-500 mt-1">{new Date(c.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
      {!readOnly && (
        <div className="flex gap-2">
          <textarea
            ref={composerRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder="Write a comment..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            type="button"
            onClick={handleSend}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 self-end"
          >
            Send
          </button>
        </div>
      )}
    </div>
  )
}
